import handlebars from 'handlebars';
import { htmlSanitize, uuid } from '@loyalty/shared-common';
import { renderToString } from 'react-dom/server';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { GenericComponent } from '../../components-tree';
import { ReactBootstrap } from '../../ReactBootstrap';
import { RssSkeleton } from './RssSkeleton';
import { Widget } from '../Widget';
import { WidgetProps } from '../render-component';
import { getClassName } from '../../utils/get-class-name';
import { parseRSSFeed } from './parse-rssfeed';

export const defaultTemplate = `
{{#with feed}}
<ul>
{{#each items}}
  <li style="font-weight: bold">{{title}}</li>
  <p>{{content}}</p>
  <img src="{{enclosure.url}}" alt="img" />
{{/each}}
</ul>
{{/with}}
`;

export interface RssComponentAttributes {
  /**
   * @deprecated
   * This prop is here for backward compatibility.
   * The prop is now translatable by the Traits form of this widget.
   */
  feedUrl?: string;
}

export interface RssComponentI18nAttributes {
  feedUrl?: string;
  template: string;
  /**
   * @deprecated
   * use `template` instead.
   */
  content: string;
}

export type RssComponent = GenericComponent<RssComponentAttributes, RssComponentI18nAttributes>;

export type RssWidgetBackendProps = WidgetProps<RssComponent>;

interface RssComponentProps {
  id: string;
  attributes: RssComponent['attributes'];
  i18nAttributes: RssComponent['i18nAttributes'];
}

function Component({ id, attributes, i18nAttributes }: RssComponentProps) {
  const { t, i18n } = useTranslation();

  const feedUrl = i18nAttributes?.[i18n.language]?.feedUrl || attributes.feedUrl;

  const rss = useSuspenseQuery({
    queryKey: ['rss-feed', feedUrl],

    queryFn: async () => {
      if (!feedUrl) {
        return t('rss.error.missingFeedUrl');
      }

      const template =
        i18nAttributes?.[i18n.language]?.template ||
        i18nAttributes?.[i18n.language]?.content ||
        defaultTemplate;
      const feed = await parseRSSFeed(feedUrl);

      const hbsTemplate = handlebars.compile(template);
      return htmlSanitize(hbsTemplate(feed));
    },
  });

  return (
    <section
      className={getClassName(['rss-component', attributes.class])}
      aria-label={t('aria.rss.label')}
      aria-description={t('aria.rss.description')}
      style={{ minHeight: '30px', width: '100%' }}
      dangerouslySetInnerHTML={{ __html: rss.data || '' }}
    />
  );
}

export function RssComponent(props: RssComponentProps) {
  return (
    <Widget loader={<RssSkeleton />}>
      <Component {...props} />
    </Widget>
  );
}

export function RssRenderer(props: RssWidgetBackendProps, document?: Document): string {
  return renderToString(
    <ReactBootstrap
      component="RssComponent"
      props={{
        id: props.options.id || props.options.attributes.id || uuid(),
        attributes: props.options.attributes,
        i18nAttributes: props.options.i18nAttributes,
      }}
      document={document}
      componentType="rss"
    />,
  );
}
